import { Injectable, computed, inject, linkedSignal, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, httpResource } from '@angular/common/http';
import { firstValueFrom, timeout, TimeoutError } from 'rxjs';
import type { UploadedTemplate } from '../../../designs/domain/entities/design.models';
import { DESIGNS_DATA } from '../../../designs/domain/infrastructure/designs.data';
import { settledValue } from '../../../designs/domain/infrastructure/resource-value';
import {
  EMPTY_PARTNER_SALES,
  type PartnerSales,
  type PartnerSalesPeriodId,
  type PartnerStudio,
} from '../entities/partner.models';
import { sessionKnown, sessionProbeContext } from '../../../../core/project-role';
import { ACCESS_READ_LIMIT_MS } from '../../../../core/route-wait';

/**
 * The studio behind the current account. Kept apart from the sales read because the root redirect and
 * the `/partner` guard ask it on every landing, long before anyone opens the sales screen.
 */
@Injectable({ providedIn: 'root' })
export class PartnerStudioResource {
  private readonly http = inject(HttpClient);
  private request?: Promise<PartnerStudio | null>;
  private readonly answer = signal<PartnerStudio | null | undefined>(undefined);

  /**
   * The same answer as a signal, so a screen that comes back renders it in its FIRST frame instead of
   * filling in a beat later. `undefined` means «not asked yet» — a reader must paint nothing then, never
   * a guess it would have to take back.
   */
  readonly studio = this.answer.asReadonly();

  /** An empty answer means «this account owns no studio», which is an answer, not a failure — hence
   *  `null`, not a throw. The 404 branch outlives the gateway that still sends it. */
  myStudio(): Promise<PartnerStudio | null> {
    // Без сессии сервер ответит 401, и этот отказ копился в аналитике как ошибка. «Нет студии» здесь —
    // не вердикт, а отсутствие вопроса: ни промис, ни сигнал его не запоминают, иначе вошедший партнёр
    // читал бы ответ, снятый до входа.
    if (!sessionKnown()) return Promise.resolve(null);

    this.request ??= this.resolve();
    return this.request;
  }

  /**
   * `undefined` in the signal is one beat, never a verdict — the shell paints no tabs while the account
   * has not answered, so this must always end in an answer. A 404 is one. Anything else is a blip and is
   * asked once more; only a second failure settles on «no studio», and the cache is dropped with it so
   * the next guard pass asks again instead of living on that fallback. A server silent past the limit is
   * not asked twice: the second wait would double the time every gate on this read holds the entry.
   */
  private async resolve(): Promise<PartnerStudio | null> {
    try {
      return this.remember(await this.ask());
    } catch (error: unknown) {
      if (error instanceof HttpErrorResponse && error.status === 404) return this.remember(null);
      if (error instanceof TimeoutError) {
        this.request = undefined;
        return this.remember(null);
      }
    }

    try {
      return this.remember(await this.ask());
    } catch (error: unknown) {
      if (!(error instanceof HttpErrorResponse) || error.status !== 404) this.request = undefined;
      return this.remember(null);
    }
  }

  private async ask(): Promise<PartnerStudio | null> {
    return (
      (await firstValueFrom(
        this.http
          .get<PartnerStudio | null>('/api/partner-studios/my', { context: sessionProbeContext() })
          .pipe(timeout(ACCESS_READ_LIMIT_MS)),
      )) ?? null
    );
  }

  private remember(studio: PartnerStudio | null): PartnerStudio | null {
    this.answer.set(studio);
    return studio;
  }
}

@Injectable({ providedIn: 'root' })
export class PartnerSalesResource {
  private readonly data = inject(DESIGNS_DATA);

  readonly period = signal<PartnerSalesPeriodId>('month');

  private readonly resource = httpResource<PartnerSales>(() => `/api/partner-studios/my/sales?period=${this.period()}`, {
    defaultValue: EMPTY_PARTNER_SALES,
  });

  /**
   * A period switch drops the resource back to its empty default until the answer lands, and the screen
   * read that default as truth: KPI counters fell to zero, the period chips left the header and the table
   * announced «продаж нет» over rows that were about to come back. The last real answer therefore stays on
   * screen until the next one replaces it. An error is not «still loading», so it hands the empty state
   * back rather than leaving another period's figures under the chip the partner just pressed.
   */
  readonly sales = linkedSignal<PartnerSales, PartnerSales>({
    // Never the raw `value` — it throws in the error state before the computation gets to ask `error()`.
    source: settledValue(this.resource, EMPTY_PARTNER_SALES),
    computation: (loaded, previous) =>
      loaded === EMPTY_PARTNER_SALES && !this.resource.error() ? (previous?.value ?? loaded) : loaded,
  });

  readonly loading = this.resource.isLoading;
  readonly failed = computed(() => !!this.resource.error());

  /** False until the first answer lands — the screen holds a dash there instead of a zero it would overwrite. */
  readonly settled = computed(() => this.sales() !== EMPTY_PARTNER_SALES);

  /** The studio owns these packs, so the owner-scoped spreads read answers — the same one the showcase uses. */
  loadSpreads(packId: string): Promise<UploadedTemplate[]> {
    return this.data.getTemplates(packId);
  }
}
